import React, { useState, useRef, useEffect } from 'react';
import { BookHeart, Plus, Trash2, ArrowLeft, ImagePlus, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/i18n';
import { useConfirm } from '../lib/ConfirmDialog';
import { uploadPhotos, deletePhoto, refreshSignedUrls } from '../lib/diaryPhotos';

const formatBigDate = (iso, lang) => {
  try {
    return format(parseISO(iso), 'EEE, d. MMMM yyyy', lang === 'de' ? { locale: de } : undefined);
  } catch {
    return iso;
  }
};

const formatShortDate = (iso) => {
  try {
    return format(parseISO(iso), 'd. MMM yyyy', { locale: de });
  } catch {
    return iso;
  }
};

const Diary = ({ data, updateData }) => {
  const entries = data.diary?.entries || [];
  const { user } = useAuth();
  const { t, lang } = useTranslation();
  const confirm = useConfirm();
  const [editingId, setEditingId] = useState(null); // id of entry being edited, or null = list view
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Refresh signed URLs once per mount so <img src> works for stored photos.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const needsRefresh = entries.some((e) =>
        (e.photos || []).some((p) => p && p.path && !p.url)
      );
      if (!needsRefresh) return;
      const updated = await Promise.all(
        entries.map(async (e) => ({
          ...e,
          photos: await refreshSignedUrls(e.photos || [])
        }))
      );
      if (!cancelled) updateData({ diary: { ...(data.diary || {}), entries: updated } });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveEntries = (next) => {
    updateData({ diary: { ...(data.diary || {}), entries: next } });
  };

  const createEntry = () => {
    const now = new Date();
    const entry = {
      id: Date.now().toString(),
      date: format(now, 'yyyy-MM-dd'),
      title: '',
      body: '',
      photos: [],
      createdAt: now.toISOString(),
      updatedAt: now.toISOString()
    };
    saveEntries([entry, ...entries]);
    setEditingId(entry.id);
  };

  const updateEntry = (id, patch) => {
    saveEntries(
      entries.map((e) =>
        e.id === id ? { ...e, ...patch, updatedAt: new Date().toISOString() } : e
      )
    );
  };

  const deleteEntry = async (id) => {
    if (!(await confirm({ message: t('diary.deleteConfirm'), danger: true }))) return;
    saveEntries(entries.filter((e) => e.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const handlePhotoUpload = async (e, entry) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !user) return;
    setUploading(true);
    try {
      const newPhotos = await uploadPhotos(user.id, entry.id, files);
      updateEntry(entry.id, { photos: [...(entry.photos || []), ...newPhotos] });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Photo upload failed', err);
      alert(t('diary.uploadFailed', { message: err.message || err }));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = async (entry, photoId) => {
    const photo = (entry.photos || []).find((p) => p.id === photoId);
    updateEntry(entry.id, {
      photos: (entry.photos || []).filter((p) => p.id !== photoId)
    });
    if (photo) await deletePhoto(photo);
  };

  // Sort entries by date desc
  const sortedEntries = [...entries].sort((a, b) =>
    (b.date || '').localeCompare(a.date || '')
  );

  const editing = editingId ? entries.find((e) => e.id === editingId) : null;

  // ===== Editor view =====
  if (editing) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setEditingId(null)}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>
          <button
            onClick={() => deleteEntry(editing.id)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
          >
            <Trash2 size={14} />
            {t('common.delete')}
          </button>
        </div>

        <div className="card space-y-5">
          {/* Big date header */}
          <div className="flex items-start justify-between gap-3 pb-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex-1">
              {formatBigDate(editing.date, lang)}
            </h2>
            <input
              type="date"
              value={editing.date}
              onChange={(e) => updateEntry(editing.id, { date: e.target.value })}
              className="text-sm text-gray-500 bg-transparent border border-gray-200 rounded px-2 py-1 hover:bg-gray-50 outline-none focus:border-primary-500"
            />
          </div>

          {/* Title */}
          <input
            type="text"
            placeholder={t('diary.titlePlaceholder')}
            value={editing.title}
            onChange={(e) => updateEntry(editing.id, { title: e.target.value })}
            className="w-full text-xl font-semibold text-gray-900 bg-transparent outline-none placeholder-gray-300 border-none px-0"
          />

          {/* Body */}
          <textarea
            placeholder={t('diary.bodyPlaceholder')}
            value={editing.body}
            onChange={(e) => updateEntry(editing.id, { body: e.target.value })}
            rows={12}
            className="w-full text-gray-800 bg-transparent outline-none placeholder-gray-300 border-none px-0 resize-none leading-relaxed"
          />

          {/* Photos */}
          {editing.photos && editing.photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {editing.photos.map((p) => (
                <div
                  key={p.id}
                  className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden"
                >
                  <img
                    src={p.url || p.dataUrl}
                    alt={p.name || ''}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removePhoto(editing, p.id)}
                    className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t('diary.removePhoto')}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Photo upload */}
          <div className="pt-2">
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg cursor-pointer">
              <ImagePlus size={16} />
              {uploading ? t('common.uploading') : t('diary.addPhoto')}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                disabled={uploading}
                onChange={(e) => handlePhotoUpload(e, editing)}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
    );
  }

  // ===== List view =====
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('diary.title')}</h2>
          <p className="text-sm sm:text-base text-gray-500">{t('diary.subtitle')}</p>
        </div>
        <button onClick={createEntry} className="btn-primary inline-flex items-center gap-2 !px-3 !py-2 sm:!px-4">
          <Plus size={16} />
          {t('diary.newEntry')}
        </button>
      </header>

      {sortedEntries.length === 0 ? (
        <div className="card text-center py-16">
          <BookHeart size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">{t('diary.empty')}</p>
          <button onClick={createEntry} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} />
            {t('diary.firstEntry')}
          </button>
        </div>
      ) : (
        <ul className="flex flex-wrap gap-3 items-start">
          {sortedEntries.map((entry) => (
            <li
              key={entry.id}
              onClick={() => setEditingId(entry.id)}
              className="cursor-pointer hover:shadow-md transition-shadow bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 px-4 py-3 aspect-square flex flex-col items-center justify-center text-center"
            >
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                {formatBigDate(entry.date, lang)}
              </h3>
              {entry.title && (
                <p className="mt-1 text-xs font-normal text-gray-500 dark:text-gray-400 italic truncate max-w-full">
                  {entry.title}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Diary;
